import { useEffect, useRef } from "react";

//@ts-ignore
import styles from "./Toast.module.scss";

import { ReactComponent as CompleteCircle } from "assets/svg/completeCircle.svg";
import { ReactComponent as InProgressCircle } from "assets/svg/inProgressCircle.svg";
import { ReactComponent as OnHoldOrange } from "assets/svg/onHoldOrange.svg";
import { ReactComponent as CancelCircle } from "assets/svg/cancelCircle.svg";
import { ReactComponent as CloseIcon } from "assets/svg/closeBox.svg";
import { ReactComponent as TrashRed } from "assets/svg/trashRed.svg";
import { ReactComponent as OrangePencil } from "assets/svg/orangePencil.svg";

import type { ToastStatus } from "../Toast/ToastProvider.d";

type CustomToastProps = {
  status: ToastStatus;
  title: string;
  message: string;
  onClose: () => void;
  duration: number;
  onUndo?: () => void;
  isCloseable?: boolean;
  onView?: () => void;
};

const statusIcons = {
  canceled: <CancelCircle className={styles.icon} />,
  completed: <CompleteCircle className={styles.icon} />,
  inProgress: <InProgressCircle className={styles.icon} />,
  onHold: <OnHoldOrange className={styles.icon} />,
  delete: <TrashRed className={styles.icon} />,
};

const statusTitles = {
  canceled: "Canceled",
  completed: "Completed",
  inProgress: "In Progress",
  delete: "Deleted",
  onHold: "On Hold"
};

const CustomToast = ({
  status = "completed",
  title,
  message,
  onClose,
  duration = 4000,
  onUndo,
  isCloseable = false,
  onView,
}: CustomToastProps) => {
  const toastRef = useRef<HTMLDivElement>(null);
  const timersRef = useRef<{
    enterTimeout?: NodeJS.Timeout;
    exitTimeout?: NodeJS.Timeout;
    mainTimer?: NodeJS.Timeout;
  }>({});

  useEffect(() => {
    const node = toastRef.current;
    if (!node) return;

    // Clear any existing timers
    if (timersRef.current.enterTimeout) {
      clearTimeout(timersRef.current.enterTimeout);
    }
    if (timersRef.current.exitTimeout) {
      clearTimeout(timersRef.current.exitTimeout);
    }
    if (timersRef.current.mainTimer) {
      clearTimeout(timersRef.current.mainTimer);
    }

    // Reset animation classes
    node.classList.remove(styles.enter, styles.exit);

    // Force reflow
    node.offsetHeight;

    // Set enter animation
    timersRef.current.enterTimeout = setTimeout(() => {
      if (node) {
        node.classList.add(styles.enter);
      }
    }, 10);

    // Set main timer for auto-close
    timersRef.current.mainTimer = setTimeout(() => {
      if (node) {
        node.classList.remove(styles.enter);
        node.classList.add(styles.exit);

        timersRef.current.exitTimeout = setTimeout(() => {
          if (onClose) {
            onClose();
          }
        }, 400);
      }
    }, duration); // 4000

    // Cleanup function
    return () => {
      if (timersRef.current.enterTimeout) {
        clearTimeout(timersRef.current.enterTimeout);
      }
      if (timersRef.current.exitTimeout) {
        clearTimeout(timersRef.current.exitTimeout);
      }
      if (timersRef.current.mainTimer) {
        clearTimeout(timersRef.current.mainTimer);
      }
    };
  }, [duration]);

  const handleClick = () => {
    // Clear timers when manually closing
    if (timersRef.current.mainTimer) {
      clearTimeout(timersRef.current.mainTimer);
    }
    if (timersRef.current.exitTimeout) {
      clearTimeout(timersRef.current.exitTimeout);
    }

    const node = toastRef.current;
    if (node) {
      node.classList.remove(styles.enter);
      node.classList.add(styles.exit);

      timersRef.current.exitTimeout = setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 400);
    }
  };

  return isCloseable ? (
    <div ref={toastRef} className={styles.toastCloseable} onClick={handleClick}>
      <div className={styles.toastHeader}>
        <div className={styles.toastHeaderContent}>
          <div
            className={styles.toastIcon}
            style={{
              backgroundColor: status === "completed" ? "#FFEFB5" : "#ffcfcf",
            }}
          >
           {status === "completed" ? <OrangePencil /> : statusIcons[status]}
          </div>

          <h3>{title}</h3>
        </div>
        <CloseIcon />
      </div>

      <div className={styles.closeableMessage}>{message}</div>

      {onView && (
        <div className={styles.closeableButton}>
          <button
            className={styles.closeableButtonUndo}
            onClick={(e) => {
              e.stopPropagation();

              if (onView) {
                onView();
              }
            }}
          >
            View Project
          </button>
        </div>
      )}
    </div>
  ) : (
    <div
      ref={toastRef}
      className={`${styles.toast} ${styles[status]}`}
      onClick={handleClick}
    >
      {statusIcons[status]}
      <div className={styles.content}>
        <div className={styles.title}>{title || statusTitles[status]}</div>
        <div className={styles.message}>{message}</div>

        {onUndo && (
          <button
            className={styles.undo}
            onClick={(e) => {
              e.stopPropagation();
              if (onUndo) {
                onUndo();
              }
              handleClick();
            }}
          >
            Undo
          </button>
        )}
      </div>
    </div>
  );
};

export default CustomToast;
